import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import Paper from '@mui/material/Paper';
import Avatar from '@mui/material/Avatar';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';
import TablePagination from '@mui/material/TablePagination';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { alpha } from '@mui/material/styles';
import {
  Phone, AccessTime, Speed, Assignment, TrendingUp, CallMade,
  Mic, Psychology, SmartToy, EmojiObjects, Whatshot,
  AutoAwesome, CalendarToday, Autorenew,
  RecordVoiceOver, SupportAgent, School, FilterAlt, Clear as ClearIcon,
  Check as CheckIcon, Timer as TimerIcon, StackedLineChart as ScoreRangeIcon,
} from '@mui/icons-material';
import {
  PieChart, Pie, Cell, Tooltip as RechartsTooltip, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend,
  Line, LineChart, ComposedChart, RadarChart, Radar, PolarGrid,
  PolarAngleAxis, PolarRadiusAxis, AreaChart, Area,
} from 'recharts';
import {
  TabPanel, StyledTabs, KPICard, SectionHeader, ChartCard,
  DataTable, StatusChip, COLORS, CHART_COLORS, PageContainer,
} from './components/ReportShared';
import DateRangeFilter, { getDefaultDateRange } from './components/DateRangeFilter';
import useReportData from './hooks/useReportData';
import { fetchCallAnalytics, fetchAICallInsights, fetchAgentScorecard } from '../../services/reportV2Service';

// ─── Tab 1: Call Analytics — color maps for chart data from API ─────────────

const OUTCOME_COLORS = {
  Answered: COLORS.teal[600],
  'No Answer': CHART_COLORS[1],
  Missed: CHART_COLORS[3],
  'Not Connected': COLORS.slate[400],
};

const DIRECTION_COLORS = {
  Outgoing: COLORS.teal[500],
  Incoming: CHART_COLORS[1],
};

// ─── Tab 2: AI Call Insights — color maps ───────────────────────────────────

const INTENT_COLORS = {
  Convertible: COLORS.green,
  'Not Sure': COLORS.amber,
  'Not Interested': COLORS.red,
};

const TEMP_COLORS = {
  'Hot Lead': COLORS.red,
  'Warm Lead': COLORS.orange,
  'Cold Lead': COLORS.blue,
};

const RECENT_CALL_INTENT_OPTIONS = ['Convertible', 'Not Sure', 'Not Interested'];
const RECENT_CALL_TEMP_OPTIONS = ['Hot', 'Warm', 'Cold', 'Not Sure'];

function parseOptionalNumber(value) {
  if (value === '' || value == null) return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

const EMPTY_RECENT_CALL_FILTERS = {
  intent: [],
  temperature: [],
  agents: [],
  durationMin: '',
  durationMax: '',
  scoreMin: '',
  scoreMax: '',
};

// ─── Tab 3: Agent Scorecard (data from API) ────────────────────────────────

// ─── Helpers ────────────────────────────────────────────────────────────────

function ScoreChip({ score }) {
  const color = score >= 7.5 ? COLORS.green : score >= 6.0 ? COLORS.amber : COLORS.red;
  return (
    <Chip
      label={score.toFixed(1)}
      size="small"
      sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 700, fontSize: '0.78rem', minWidth: 44 }}
    />
  );
}

function IntentChip({ intent }) {
  const map = {
    Convertible: COLORS.green,
    'Not Sure': COLORS.amber,
    'Not Interested': COLORS.red,
  };
  return <StatusChip label={intent} color={map[intent] || COLORS.slate[400]} />;
}

function TempChip({ temp }) {
  const map = { Hot: COLORS.red, Warm: COLORS.orange, Cold: COLORS.blue };
  return <StatusChip label={temp} color={map[temp] || COLORS.slate[400]} />;
}

function InsightCard({ insight }) {
  const typeColor = {
    'Critical Gap': COLORS.red,
    'Quick Win': COLORS.green,
    Strength: COLORS.teal[600],
    Improvement: COLORS.amber,
  };
  return (
    <Card sx={{ borderRadius: 2, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid', borderColor: 'grey.100', mb: 2 }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <Chip label={insight.type} size="small" sx={{ bgcolor: alpha(typeColor[insight.type] || COLORS.slate[400], 0.12), color: typeColor[insight.type], fontWeight: 700, fontSize: '0.72rem' }} />
          <Chip label={insight.category} size="small" variant="outlined" sx={{ fontSize: '0.72rem', borderColor: COLORS.slate[300] }} />
          <Typography variant="caption" sx={{ color: COLORS.slate[500], ml: 'auto' }}>{insight.agent} · {(insight.confidence * 100).toFixed(0)}% confidence</Typography>
        </Box>
        <Typography variant="body2" sx={{ color: COLORS.slate[700], mb: 1.5, lineHeight: 1.6 }}>{insight.message}</Typography>
        <Box sx={{ bgcolor: alpha(COLORS.teal[500], 0.06), borderRadius: 1.5, p: 1.5, borderLeft: `3px solid ${COLORS.teal[500]}` }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: COLORS.teal[700], display: 'block', mb: 0.3 }}>Recommended Action</Typography>
          <Typography variant="body2" sx={{ color: COLORS.slate[600], fontSize: '0.82rem' }}>{insight.action}</Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function WeeklyScoreCell({ score, delta }) {
  const scoreColor = score >= 7.5 ? COLORS.green : score >= 6.0 ? COLORS.amber : COLORS.red;
  const deltaColor = delta > 0 ? COLORS.green : delta < 0 ? COLORS.red : COLORS.slate[400];
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <Chip
        label={score.toFixed(1)}
        size="small"
        sx={{ bgcolor: alpha(scoreColor, 0.12), color: scoreColor, fontWeight: 700, fontSize: '0.76rem', minWidth: 42 }}
      />
      <Typography
        variant="caption"
        sx={{ color: deltaColor, fontWeight: 700, fontSize: '0.65rem', minWidth: 28, textAlign: 'center' }}
      >
        {delta > 0 ? `▲${delta.toFixed(1)}` : delta < 0 ? `▼${Math.abs(delta).toFixed(1)}` : '—'}
      </Typography>
    </Box>
  );
}

function MiniProgress({ value, max = 100, color = COLORS.teal[500] }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LinearProgress variant="determinate" value={Math.min((value / max) * 100, 100)} sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: alpha(color, 0.15), '& .MuiLinearProgress-bar': { bgcolor: color, borderRadius: 3 } }} />
      <Typography variant="caption" sx={{ fontWeight: 600, minWidth: 28, color: COLORS.slate[600] }}>{value}%</Typography>
    </Box>
  );
}

function getNextSundayDate(from = new Date()) {
  // Returns the next upcoming Sunday date (skips to next week if today is Sunday).
  const day = from.getDay(); // 0 (Sun) - 6 (Sat)
  const daysUntilNext = (7 - day) % 7 || 7;
  const d = new Date(from);
  d.setDate(from.getDate() + daysUntilNext);
  d.setHours(0, 0, 0, 0);
  return d;
}

function formatShortDate(d) {
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// ─── Main Component ─────────────────────────────────────────────────────────

export default function ConversationIntelligence() {
  const [tabValue, setTabValue] = useState(0);
  const TABS = ['Call Analytics', 'AI Call Insights', 'Agent Scorecard'];
  const [coachingTabValue, setCoachingTabValue] = useState(0);
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [recentCallsPage, setRecentCallsPage] = useState(1);
  const RECENT_CALLS_PAGE_SIZE = 100;
  const [draftFilters, setDraftFilters] = useState(() => ({ ...EMPTY_RECENT_CALL_FILTERS }));
  const [appliedFilters, setAppliedFilters] = useState(() => ({ ...EMPTY_RECENT_CALL_FILTERS }));
  const nextRefreshDate = formatShortDate(getNextSundayDate());

  const filtersDirty = React.useMemo(
    () => JSON.stringify(draftFilters) !== JSON.stringify(appliedFilters),
    [draftFilters, appliedFilters],
  );

  const applyRecentCallFilters = React.useCallback((e) => {
    if (e?.preventDefault) e.preventDefault();
    setAppliedFilters({ ...draftFilters });
    setRecentCallsPage(1);
  }, [draftFilters]);

  const clearRecentCallFilters = React.useCallback(() => {
    setDraftFilters({ ...EMPTY_RECENT_CALL_FILTERS });
    setAppliedFilters({ ...EMPTY_RECENT_CALL_FILTERS });
    setRecentCallsPage(1);
  }, []);

  React.useEffect(() => {
    setRecentCallsPage(1);
    setDraftFilters({ ...EMPTY_RECENT_CALL_FILTERS });
    setAppliedFilters({ ...EMPTY_RECENT_CALL_FILTERS });
  }, [dateRange.startDate, dateRange.endDate]);

  const {
    data: callData,
    loading: callLoading,
  } = useReportData(fetchCallAnalytics, dateRange, {}, tabValue === 0);

  const {
    data: aiData,
    loading: aiLoading,
  } = useReportData(fetchAICallInsights, dateRange, {}, tabValue === 1);

  // Agent Scorecard: no date range needed, uses a dummy stable dateRange so hook fires once
  const scorecardDateRange = React.useMemo(() => ({ startDate: '2000-01-01', endDate: '2099-12-31' }), []);
  const {
    data: scorecardData,
    loading: scorecardLoading,
  } = useReportData(fetchAgentScorecard, scorecardDateRange, {}, tabValue === 2);

  const kpis = callData?.kpis || {};
  const callOutcomeData = (callData?.callOutcomes || []).map((d) => ({
    ...d,
    color: OUTCOME_COLORS[d.name] || COLORS.slate[400],
  }));
  const callDirectionData = (callData?.callDirections || []).map((d) => ({
    ...d,
    color: DIRECTION_COLORS[d.name] || COLORS.slate[400],
  }));
  const hourlyCallData = (callData?.hourlyCallData || []).map((d) => ({
    ...d,
    leadTalkTime: d.leadTalkTime ?? 0,
  }));
  const dailyCallTrend = callData?.dailyCallTrend || [];
  const agentCallData = callData?.agentCallData || [];

  const aiKpis = aiData?.kpis || {};
  const conversionIntentData = (aiData?.conversionIntent || []).map((d) => ({
    ...d,
    color: INTENT_COLORS[d.name] || COLORS.slate[400],
  }));
  const leadTempData = (aiData?.leadTemperature || []).map((d) => ({
    ...d,
    color: TEMP_COLORS[d.name] || COLORS.slate[400],
  }));
  const conversionIntentTotal = React.useMemo(
    () => conversionIntentData.reduce((s, d) => s + (Number(d.value) || 0), 0),
    [conversionIntentData],
  );
  const leadTempTotal = React.useMemo(
    () => leadTempData.reduce((s, d) => s + (Number(d.value) || 0), 0),
    [leadTempData],
  );
  const sentimentTrend = aiData?.sentimentTrend || [];
  const topTopicsData = aiData?.topTopics || [];
  const allRecentAICalls = aiData?.recentCalls || [];
  const recentCallAgentOptions = React.useMemo(() => {
    const names = new Set(allRecentAICalls.map((c) => c.agent).filter(Boolean));
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [allRecentAICalls]);

  const filteredRecentCalls = React.useMemo(() => {
    const dMin = parseOptionalNumber(appliedFilters.durationMin);
    const dMax = parseOptionalNumber(appliedFilters.durationMax);
    const sMin = parseOptionalNumber(appliedFilters.scoreMin);
    const sMax = parseOptionalNumber(appliedFilters.scoreMax);

    return allRecentAICalls.filter((row) => {
      const sec = row.durationSeconds ?? 0;
      if (dMin != null && sec < dMin) return false;
      if (dMax != null && sec > dMax) return false;
      if (appliedFilters.intent.length && !appliedFilters.intent.includes(row.intent)) return false;
      if (appliedFilters.temperature.length && !appliedFilters.temperature.includes(row.temperature)) return false;
      if (appliedFilters.agents.length && !appliedFilters.agents.includes(row.agent)) return false;
      const raw = Number(row.leadScore);
      const scoreOutOf10 = Number.isFinite(raw) ? raw / 10 : 0;
      if (sMin != null && scoreOutOf10 < sMin) return false;
      if (sMax != null && scoreOutOf10 > sMax) return false;
      return true;
    });
  }, [allRecentAICalls, appliedFilters]);

  const recentCallsTotal = filteredRecentCalls.length;
  const recentAICalls = React.useMemo(() => {
    const start = (recentCallsPage - 1) * RECENT_CALLS_PAGE_SIZE;
    return filteredRecentCalls.slice(start, start + RECENT_CALLS_PAGE_SIZE);
  }, [filteredRecentCalls, recentCallsPage, RECENT_CALLS_PAGE_SIZE]);

  React.useEffect(() => {
    if (recentCallsTotal === 0) return;
    const maxPage = Math.max(1, Math.ceil(recentCallsTotal / RECENT_CALLS_PAGE_SIZE));
    if (recentCallsPage > maxPage) {
      setRecentCallsPage(maxPage);
    }
  }, [recentCallsTotal, recentCallsPage, RECENT_CALLS_PAGE_SIZE]);

  // Agent Scorecard derived data
  const scKpis = scorecardData?.kpis || {};
  const scReportPeriod = scorecardData?.reportPeriod || '';
  const scTeamMetric = scorecardData?.teamMetricWeekly || [];
  const scAgentProgress = scorecardData?.agentWeeklyProgress || [];
  const scWeeklyTrend = scorecardData?.weeklyOverallTrend || [];
  const scTrendAgents = scorecardData?.weeklyTrendAgents || [];
  const scCoachingInsights = scorecardData?.coachingInsights || [];
  const scCoachingAgents = React.useMemo(
    () => Array.from(new Set(scCoachingInsights.map((i) => i.agent))),
    [scCoachingInsights],
  );

  return (
    <PageContainer>
      <StyledTabs value={tabValue} onChange={(_, v) => setTabValue(v)} tabs={TABS} />

      {/* ── Tab 1: Call Analytics ─────────────────────────────────────── */}
      <TabPanel value={tabValue} index={0}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              Call Analytics Overview
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Comprehensive call performance metrics across all agents
            </Typography>
          </Box>
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
        </Box>

        {callLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: COLORS.teal[600] }} />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KPICard title="Total Calls" value={(kpis.totalCalls ?? 0).toLocaleString()} subtitle={`${kpis.workingDays ?? '-'} days`} icon={Phone} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KPICard title="Connected" value={(kpis.connected ?? 0).toLocaleString()} subtitle={`${kpis.connectRate ?? 0}% connect rate`} icon={CallMade} color={COLORS.green} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KPICard title="Total Talk Time" value={kpis.totalTalkTime || '0m 0s'} subtitle="Across all agents" icon={AccessTime} color={CHART_COLORS[1]} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KPICard title="Avg Talk Time" value={kpis.avgTalkTime || '0m 0s'} subtitle="Per connected call" icon={Mic} color={CHART_COLORS[2]} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KPICard title="Avg Calls/Day" value={(kpis.avgCallsPerDay ?? 0).toLocaleString()} subtitle={`${kpis.workingDays ?? '-'} days`} icon={Speed} color={CHART_COLORS[3]} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KPICard title="Lead Coverage" value={`${kpis.leadCoverage ?? 0}%`} subtitle="Unique leads contacted" icon={Assignment} color={COLORS.teal[600]} />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <ChartCard title="Call Outcomes" subtitle="Distribution by call status" height={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={callOutcomeData} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2} dataKey="value" nameKey="name">
                        {callOutcomeData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <RechartsTooltip formatter={(v) => v.toLocaleString()} contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                      <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <ChartCard title="Call Direction Split" subtitle="Outgoing vs Incoming" height={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie data={callDirectionData} cx="50%" cy="50%" innerRadius={55} outerRadius={95} paddingAngle={2} dataKey="value" nameKey="name">
                        {callDirectionData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <RechartsTooltip formatter={(v) => v.toLocaleString()} contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                      <Legend verticalAlign="bottom" iconType="circle" wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <ChartCard title="Daily Call Volume" subtitle="Calls & connected by day of week" height={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dailyCallTrend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                      <YAxis tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                      <RechartsTooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="calls" fill={COLORS.teal[500]} name="Total" radius={[3, 3, 0, 0]} />
                      <Bar dataKey="connected" fill={CHART_COLORS[1]} name="Connected" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12 }}>
                <ChartCard title="Hourly Call Activity" subtitle="Contacted, connected, total talk time and lead talk time (minutes) by hour of day" height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={hourlyCallData} margin={{ top: 5, right: 24, left: 0, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                      <XAxis dataKey="hour" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                      <RechartsTooltip
                        formatter={(value, name) => {
                          if (name === 'Talk Time' || name === 'Lead Talk Time') {
                            return [`${Number(value).toLocaleString()} min`, name];
                          }
                          return [value, name];
                        }}
                        contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }}
                      />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar yAxisId="left" dataKey="contacted" fill={COLORS.teal[400]} name="Contacted" radius={[3, 3, 0, 0]} />
                      <Bar yAxisId="left" dataKey="connected" fill={COLORS.teal[700]} name="Connected" radius={[3, 3, 0, 0]} />
                      <Line yAxisId="right" type="monotone" dataKey="talkTime" stroke={CHART_COLORS[3]} strokeWidth={2} name="Talk Time" dot={{ r: 3 }} />
                      <Line yAxisId="right" type="monotone" dataKey="leadTalkTime" stroke={COLORS.amber} strokeWidth={2} name="Lead Talk Time" dot={{ r: 3 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
            </Grid>

            <SectionHeader title="Agent Call Performance" subtitle="Individual agent metrics for the period" />
            <DataTable
              columns={[
                { label: 'Agent', field: 'agent' },
                { label: 'Total Calls', field: 'totalCalls', align: 'right' },
                { label: 'New', field: 'newCalls', align: 'right' },
                { label: 'Follow-up', field: 'followUpCalls', align: 'right' },
                { label: 'Not Connected', field: 'notConnected', align: 'right' },
                { label: 'Connect %', field: 'connectedRate', align: 'right', render: (v) => <MiniProgress value={v} color={v >= 80 ? COLORS.green : v >= 70 ? COLORS.amber : COLORS.red} /> },
                { label: 'Avg Talk Time', field: 'avgTalkTime', align: 'right' },
                { label: 'Talktime Overall (Min)', field: 'talkTimeMin', align: 'right', render: (v) => v.toLocaleString() },
                { label: 'Talktime Leads (Min)', field: 'talkTimeLeadsMin', align: 'right', render: (v) => v.toLocaleString() },
              ]}
              rows={agentCallData}
            />
          </>
        )}
      </TabPanel>

      {/* ── Tab 2: AI Call Insights ───────────────────────────────────── */}
      <TabPanel value={tabValue} index={1}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800], mb: 0.5 }}>
              AI-Powered Call Insights
            </Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
              Automated analysis of call recordings using AI transcription & sentiment detection
            </Typography>
          </Box>
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
        </Box>

        {aiLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: COLORS.teal[600] }} />
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KPICard title="Calls Analyzed" value={(aiKpis.callsAnalyzed ?? 0).toLocaleString()} subtitle="AI processed" icon={SmartToy} color={CHART_COLORS[2]} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KPICard title="Avg AI Score" value={aiKpis.avgAIScore ?? '0'} subtitle="Out of 10" icon={Psychology} color={COLORS.teal[600]} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KPICard title="Hot Leads Found" value={(aiKpis.hotLeadsFound ?? 0).toLocaleString()} subtitle={`${aiKpis.hotLeadsPct ?? 0}% of analyzed`} icon={Whatshot} color={COLORS.red} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KPICard title="Convertible %" value={`${aiKpis.convertiblePct ?? 0}%`} subtitle={`${(aiKpis.convertibleCount ?? 0).toLocaleString()} leads`} icon={TrendingUp} color={COLORS.green} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KPICard title="Positive Sentiment" value={`${aiKpis.positiveSentiment ?? 0}%`} subtitle="Avg across calls" icon={EmojiObjects} color={CHART_COLORS[1]} />
              </Grid>
              <Grid size={{ xs: 6, sm: 4, md: 2 }}>
                <KPICard title="Auto-Tasks Created" value={(aiKpis.autoTasksCreated ?? 0).toLocaleString()} subtitle="From AI insights" icon={Assignment} color={CHART_COLORS[3]} />
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <ChartCard title="Conversion Intent" subtitle="AI-detected buying intent from calls" height={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                      <Pie data={conversionIntentData} cx="50%" cy="50%" innerRadius={55} outerRadius={88} paddingAngle={3} dataKey="value" nameKey="name" labelLine={false}>
                        {conversionIntentData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value, name) => {
                          const v = Number(value);
                          const pct = conversionIntentTotal > 0 ? ((v / conversionIntentTotal) * 100).toFixed(0) : '0';
                          return [`${value} (${pct}%)`, name];
                        }}
                        contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }}
                      />
                      <Legend
                        verticalAlign="bottom"
                        iconType="circle"
                        wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                        formatter={(value, entry) => {
                          const v = Number(entry?.payload?.value ?? 0);
                          const pct = conversionIntentTotal > 0 ? ((v / conversionIntentTotal) * 100).toFixed(0) : '0';
                          return `${value}: ${pct}%`;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                <ChartCard title="Lead Temperature" subtitle="AI-scored lead heat from conversations" height={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
                      <Pie data={leadTempData} cx="50%" cy="50%" innerRadius={55} outerRadius={88} paddingAngle={3} dataKey="value" nameKey="name" labelLine={false}>
                        {leadTempData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                      </Pie>
                      <RechartsTooltip
                        formatter={(value, name) => {
                          const v = Number(value);
                          const pct = leadTempTotal > 0 ? ((v / leadTempTotal) * 100).toFixed(0) : '0';
                          return [`${value} (${pct}%)`, name];
                        }}
                        contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }}
                      />
                      <Legend
                        verticalAlign="bottom"
                        iconType="circle"
                        wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                        formatter={(value, entry) => {
                          const v = Number(entry?.payload?.value ?? 0);
                          const pct = leadTempTotal > 0 ? ((v / leadTempTotal) * 100).toFixed(0) : '0';
                          return `${value}: ${pct}%`;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
              <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                <ChartCard title="Sentiment Trend" subtitle="Weekly positive / neutral / negative %" height={280}>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={sentimentTrend} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                      <XAxis dataKey="week" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                      <YAxis tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                      <RechartsTooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} formatter={(v) => `${v}%`} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Area type="monotone" dataKey="positive" stackId="1" stroke={COLORS.green} fill={alpha(COLORS.green, 0.3)} name="Positive" />
                      <Area type="monotone" dataKey="neutral" stackId="1" stroke={COLORS.amber} fill={alpha(COLORS.amber, 0.3)} name="Neutral" />
                      <Area type="monotone" dataKey="negative" stackId="1" stroke={COLORS.red} fill={alpha(COLORS.red, 0.3)} name="Negative" />
                    </AreaChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 12 }}>
                <ChartCard title="Top Discussed Topics" subtitle="Most mentioned topics in calls with average sentiment score" height={300}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={topTopicsData} layout="vertical" margin={{ top: 5, right: 30, left: 120, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                      <XAxis type="number" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                      <YAxis type="category" dataKey="topic" width={110} tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                      <RechartsTooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="mentions" fill={COLORS.teal[500]} name="Mentions" radius={[0, 4, 4, 0]} />
                      <Bar dataKey="sentiment" fill={CHART_COLORS[1]} name="Sentiment %" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Grid>
            </Grid>

            <SectionHeader
              title="Recent AI-Analyzed Calls"
              subtitle={
                allRecentAICalls.length !== recentCallsTotal
                  ? `Showing ${recentCallsTotal.toLocaleString()} of ${allRecentAICalls.length.toLocaleString()} calls in range — hover summary for full text`
                  : 'Latest calls with AI-generated summaries, lead scoring, and next actions — hover summary for full text'
              }
            />
            <Paper
              component="form"
              onSubmit={applyRecentCallFilters}
              elevation={0}
              sx={{
                mb: 2.5,
                borderRadius: 3,
                overflow: 'hidden',
                border: `1px solid ${alpha(COLORS.teal[600], 0.2)}`,
                boxShadow: '0 8px 32px rgba(15, 118, 110, 0.08)',
                background: `linear-gradient(180deg, ${alpha(COLORS.teal[50], 0.9)} 0%, #fff 48%)`,
              }}
            >
              <Box
                sx={{
                  height: 4,
                  background: `linear-gradient(90deg, ${COLORS.teal[500]}, ${COLORS.teal[700]}, ${CHART_COLORS[1]})`,
                }}
              />
              <Box sx={{ p: { xs: 2, sm: 2.5 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'flex-start',
                    gap: 2,
                    mb: 2.5,
                  }}
                >
                  <Avatar
                    sx={{
                      width: 44,
                      height: 44,
                      bgcolor: alpha(COLORS.teal[600], 0.12),
                      color: COLORS.teal[700],
                    }}
                  >
                    <FilterAlt sx={{ fontSize: '1.35rem' }} />
                  </Avatar>
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: COLORS.slate[800], fontSize: '1.05rem', mb: 0.25 }}>
                      Refine results
                    </Typography>
                    <Typography variant="body2" sx={{ color: COLORS.slate[500], fontSize: '0.82rem', lineHeight: 1.5 }}>
                      Adjust duration, AI score (1–10, same as the table), intent, temperature, or agent — then click Apply.
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', ml: { xs: 0, sm: 'auto' } }}>
                    {filtersDirty && (
                      <Chip
                        label="Unsaved changes"
                        size="small"
                        sx={{
                          bgcolor: alpha(COLORS.amber, 0.12),
                          color: COLORS.amber,
                          fontWeight: 600,
                          fontSize: '0.72rem',
                        }}
                      />
                    )}
                    <Button
                      type="button"
                      variant="outlined"
                      size="medium"
                      startIcon={<ClearIcon sx={{ fontSize: '1.1rem !important' }} />}
                      onClick={clearRecentCallFilters}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderColor: COLORS.slate[300],
                        color: COLORS.slate[700],
                        '&:hover': { borderColor: COLORS.slate[400], bgcolor: COLORS.slate[50] },
                      }}
                    >
                      Clear
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      size="medium"
                      disabled={!filtersDirty}
                      startIcon={<CheckIcon sx={{ fontSize: '1.1rem !important' }} />}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 2.5,
                        boxShadow: `0 4px 14px ${alpha(COLORS.teal[600], 0.35)}`,
                        bgcolor: COLORS.teal[600],
                        '&:hover': { bgcolor: COLORS.teal[700] },
                        '&.Mui-disabled': { bgcolor: COLORS.slate[200], color: COLORS.slate[500] },
                      }}
                    >
                      Apply
                    </Button>
                  </Box>
                </Box>

                <Grid container spacing={2} alignItems="flex-end">
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Duration min"
                      type="number"
                      inputProps={{ min: 0 }}
                      value={draftFilters.durationMin}
                      onChange={(e) => setDraftFilters((p) => ({ ...p, durationMin: e.target.value }))}
                      placeholder="Any"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="caption" sx={{ color: COLORS.slate[400], fontWeight: 600 }}>sec</Typography>
                          </InputAdornment>
                        ),
                        startAdornment: (
                          <InputAdornment position="start">
                            <TimerIcon sx={{ fontSize: '1.1rem', color: COLORS.teal[600], opacity: 0.85 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Duration max"
                      type="number"
                      inputProps={{ min: 0 }}
                      value={draftFilters.durationMax}
                      onChange={(e) => setDraftFilters((p) => ({ ...p, durationMax: e.target.value }))}
                      placeholder="Any"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="caption" sx={{ color: COLORS.slate[400], fontWeight: 600 }}>sec</Typography>
                          </InputAdornment>
                        ),
                        startAdornment: (
                          <InputAdornment position="start">
                            <TimerIcon sx={{ fontSize: '1.1rem', color: COLORS.teal[600], opacity: 0.85 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="AI score min"
                      type="number"
                      inputProps={{ min: 0, max: 10, step: 0.1 }}
                      value={draftFilters.scoreMin}
                      onChange={(e) => setDraftFilters((p) => ({ ...p, scoreMin: e.target.value }))}
                      placeholder="Any"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="caption" sx={{ color: COLORS.slate[400], fontWeight: 600 }}>/10</Typography>
                          </InputAdornment>
                        ),
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScoreRangeIcon sx={{ fontSize: '1.1rem', color: COLORS.teal[600], opacity: 0.85 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <TextField
                      size="small"
                      fullWidth
                      label="AI score max"
                      type="number"
                      inputProps={{ min: 0, max: 10, step: 0.1 }}
                      value={draftFilters.scoreMax}
                      onChange={(e) => setDraftFilters((p) => ({ ...p, scoreMax: e.target.value }))}
                      placeholder="Any"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Typography variant="caption" sx={{ color: COLORS.slate[400], fontWeight: 600 }}>/10</Typography>
                          </InputAdornment>
                        ),
                        startAdornment: (
                          <InputAdornment position="start">
                            <ScoreRangeIcon sx={{ fontSize: '1.1rem', color: COLORS.teal[600], opacity: 0.85 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}>
                      <InputLabel id="rc-filter-intent">Intent</InputLabel>
                      <Select
                        labelId="rc-filter-intent"
                        multiple
                        value={draftFilters.intent}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraftFilters((p) => ({
                            ...p,
                            intent: typeof v === 'string' ? v.split(',') : v,
                          }));
                        }}
                        input={<OutlinedInput label="Intent" />}
                        renderValue={(sel) => (sel.length === 0 ? 'All intents' : sel.join(', '))}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 280, borderRadius: 2 } } }}
                      >
                        {RECENT_CALL_INTENT_OPTIONS.map((name) => (
                          <MenuItem key={name} value={name} dense>{name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                    <FormControl size="small" fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}>
                      <InputLabel id="rc-filter-temp">Temperature</InputLabel>
                      <Select
                        labelId="rc-filter-temp"
                        multiple
                        value={draftFilters.temperature}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraftFilters((p) => ({
                            ...p,
                            temperature: typeof v === 'string' ? v.split(',') : v,
                          }));
                        }}
                        input={<OutlinedInput label="Temperature" />}
                        renderValue={(sel) => (sel.length === 0 ? 'All temperatures' : sel.join(', '))}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 280, borderRadius: 2 } } }}
                      >
                        {RECENT_CALL_TEMP_OPTIONS.map((name) => (
                          <MenuItem key={name} value={name} dense>{name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl
                      size="small"
                      fullWidth
                      disabled={recentCallAgentOptions.length === 0}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2, bgcolor: 'background.paper' } }}
                    >
                      <InputLabel id="rc-filter-agent">Agent</InputLabel>
                      <Select
                        labelId="rc-filter-agent"
                        multiple
                        value={draftFilters.agents}
                        onChange={(e) => {
                          const v = e.target.value;
                          setDraftFilters((p) => ({
                            ...p,
                            agents: typeof v === 'string' ? v.split(',') : v,
                          }));
                        }}
                        input={<OutlinedInput label="Agent" />}
                        renderValue={(sel) => (sel.length === 0 ? 'All agents' : `${sel.length} selected`)}
                        MenuProps={{ PaperProps: { sx: { maxHeight: 320, borderRadius: 2 } } }}
                      >
                        {recentCallAgentOptions.map((name) => (
                          <MenuItem key={name} value={name} dense>{name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
            <Box sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'grey.100', boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
            <DataTable
              maxHeight={560}
              sx={{ boxShadow: 'none', border: 'none', borderRadius: 0 }}
              columns={[
                { label: 'ID', field: 'id', render: (v) => <Box sx={{ whiteSpace: 'nowrap' }}>{v}</Box> },
                {
                  label: 'Lead',
                  field: 'lead',
                  render: (v, row) => (row.leadId != null ? (
                    <Link component={RouterLink} to={`/leads/${row.leadId}`} underline="hover" sx={{ fontWeight: 600, fontSize: '0.82rem', color: COLORS.teal[700] }}>
                      {v}
                    </Link>
                  ) : v),
                },
                { label: 'Agent', field: 'agent' },
                {
                  label: 'Date',
                  field: 'date',
                  render: (v) => (
                    <Typography variant="caption" sx={{ color: COLORS.slate[700], whiteSpace: 'nowrap' }}>
                      {v || '—'}
                    </Typography>
                  ),
                },
                {
                  label: 'Recording',
                  field: 'recordingUrl',
                  render: (v) => (
                    v ? (
                      <audio
                        controls
                        preload="none"
                        src={v}
                        style={{ height: 32, width: 200, maxWidth: 'min(220px, 36vw)' }}
                      />
                    ) : (
                      <Typography variant="caption" sx={{ color: COLORS.slate[400] }}>—</Typography>
                    )
                  ),
                },
                { label: 'Duration', field: 'duration', align: 'right' },
                { label: 'Score', field: 'leadScore', align: 'center', render: (v) => <ScoreChip score={v / 10} /> },
                { label: 'Intent', field: 'intent', align: 'center', render: (v) => <IntentChip intent={v} /> },
                { label: 'Temp', field: 'temperature', align: 'center', render: (v) => <TempChip temp={v} /> },
                { label: 'Budget', field: 'budget' },
                {
                  label: 'AI Summary',
                  field: 'summary',
                  render: (v, row) => {
                    const full = (row.fullSummary || v || '').trim();
                    const display = v || '—';
                    const summaryCell = (
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{
                          color: COLORS.slate[600],
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          lineHeight: 1.5,
                          maxWidth: 320,
                        }}
                      >
                        {display}
                      </Typography>
                    );
                    if (!full) {
                      return summaryCell;
                    }
                    return (
                      <Tooltip
                        title={full}
                        placement="top-start"
                        enterDelay={400}
                        slotProps={{
                          tooltip: {
                            sx: {
                              maxWidth: 520,
                              bgcolor: COLORS.slate[800],
                              fontSize: '0.8rem',
                              lineHeight: 1.5,
                              py: 1.25,
                              px: 1.5,
                            },
                          },
                        }}
                      >
                        <Box sx={{ cursor: 'help', maxWidth: 320 }}>{summaryCell}</Box>
                      </Tooltip>
                    );
                  },
                },
                { label: 'Next Action', field: 'nextAction', render: (v) => <Typography variant="caption" sx={{ color: COLORS.teal[700], fontWeight: 600, fontSize: '0.72rem' }}>{v}</Typography> },
              ]}
              rows={recentAICalls}
            />
            <TablePagination
              component="div"
              count={recentCallsTotal}
              page={recentCallsPage - 1}
              onPageChange={(e, newPage) => {
                e.preventDefault();
                setRecentCallsPage(newPage + 1);
              }}
              rowsPerPage={RECENT_CALLS_PAGE_SIZE}
              rowsPerPageOptions={[RECENT_CALLS_PAGE_SIZE]}
              onRowsPerPageChange={() => {}}
              sx={{ borderTop: `1px solid ${COLORS.slate[200]}`, bgcolor: COLORS.slate[50] }}
            />
            </Box>
          </>
        )}
      </TabPanel>

      {/* ── Tab 3: Agent Scorecard ────────────────────────────────────── */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1.5, mb: 0.5 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: COLORS.slate[800] }}>
              AI Agent Scorecard
            </Typography>
            <Chip
              icon={<Autorenew sx={{ fontSize: '0.8rem !important' }} />}
              label={`Refreshes every Sunday night (Next: ${nextRefreshDate})`}
              size="small"
              sx={{ bgcolor: alpha(COLORS.teal[600], 0.08), color: COLORS.teal[700], fontWeight: 600, fontSize: '0.72rem', height: 24 }}
            />
          </Box>
          {scReportPeriod && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
              <CalendarToday sx={{ color: COLORS.slate[400], fontSize: '0.85rem' }} />
              <Typography variant="body2" sx={{ color: COLORS.slate[500] }}>
                Report Period: <Box component="span" sx={{ fontWeight: 600, color: COLORS.slate[700] }}>{scReportPeriod}</Box>
              </Typography>
            </Box>
          )}
        </Box>

        {scorecardLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress sx={{ color: COLORS.teal[600] }} />
          </Box>
        ) : !scAgentProgress.length ? (
          <Box sx={{ textAlign: 'center', py: 10 }}>
            <Typography variant="h6" sx={{ color: COLORS.slate[400], mb: 1 }}>No scorecard data available yet</Typography>
            <Typography variant="body2" sx={{ color: COLORS.slate[400] }}>Reports are generated every Sunday night at 11 PM</Typography>
          </Box>
        ) : (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid size={{ xs: 6, sm: 3 }}>
                <KPICard title="Team Avg Score" value={String(scKpis.teamAvgScore ?? 0)} subtitle="Out of 10" icon={School} color={COLORS.teal[600]} trend={scKpis.teamAvgDelta > 0 ? 'up' : scKpis.teamAvgDelta < 0 ? 'down' : undefined} trendValue={scKpis.teamAvgDelta > 0 ? `+${scKpis.teamAvgDelta}` : scKpis.teamAvgDelta < 0 ? String(scKpis.teamAvgDelta) : undefined} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <KPICard title="Top Performer" value={scKpis.topPerformer || '-'} subtitle={`Score: ${scKpis.topPerformerScore ?? 0}`} icon={SupportAgent} color={COLORS.green} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <KPICard title="Biggest Gap" value={scKpis.biggestGap || '-'} subtitle={`Avg: ${scKpis.biggestGapScore ?? 0} / 10`} icon={TrendingUp} color={COLORS.red} />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <KPICard title="Calls Analyzed" value={String(scKpis.callsAnalyzed ?? 0)} subtitle="This week" icon={RecordVoiceOver} color={CHART_COLORS[1]} />
              </Grid>
            </Grid>

            <ChartCard title="Team Skill Overview" subtitle="Average score per skill metric — this week vs last week" height={280}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={scTeamMetric} layout="vertical" margin={{ top: 5, right: 30, left: 100, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                  <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                  <YAxis type="category" dataKey="metric" width={90} tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                  <RechartsTooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} formatter={(v) => `${Number(v).toFixed(1)} / 10`} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="lastWeek" fill={COLORS.slate[300]} name="Previous Week" radius={[0, 3, 3, 0]} barSize={10} />
                  <Bar dataKey="thisWeek" name="This Week" radius={[0, 3, 3, 0]} barSize={10}>
                    {scTeamMetric.map((entry, i) => (
                      <Cell key={i} fill={entry.thisWeek >= 7.5 ? COLORS.green : entry.thisWeek >= 6.0 ? COLORS.teal[500] : COLORS.red} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <Box sx={{ mt: 3, mb: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <SectionHeader title="Agent Rankings" subtitle="Scores across 8 skill categories with weekly change — ▲ improved · ▼ declined · — no change" />
              <Box sx={{ display: 'flex', gap: 1 }}>
                {(() => {
                  const improving = scAgentProgress.filter((a) => a.overallDelta > 0).length;
                  const declining = scAgentProgress.filter((a) => a.overallDelta < 0).length;
                  return (
                    <>
                      <Chip label={`${improving} improving`} size="small" sx={{ bgcolor: alpha(COLORS.green, 0.1), color: COLORS.green, fontWeight: 600, fontSize: '0.72rem' }} />
                      <Chip label={`${declining} declining`} size="small" sx={{ bgcolor: alpha(COLORS.red, 0.1), color: COLORS.red, fontWeight: 600, fontSize: '0.72rem' }} />
                    </>
                  );
                })()}
              </Box>
            </Box>
            <DataTable
              columns={[
                { label: 'Agent', field: 'agent' },
                { label: 'Overall', field: 'overall', align: 'center', render: (v, row) => <WeeklyScoreCell score={v} delta={row.overallDelta} /> },
                { label: 'Discovery', field: 'discovery', align: 'center', render: (v, row) => <WeeklyScoreCell score={v} delta={row.discoveryDelta} /> },
                { label: 'Qualification', field: 'qualification', align: 'center', render: (v, row) => <WeeklyScoreCell score={v} delta={row.qualificationDelta} /> },
                { label: 'Rapport', field: 'rapport', align: 'center', render: (v, row) => <WeeklyScoreCell score={v} delta={row.rapportDelta} /> },
                { label: 'Product', field: 'product', align: 'center', render: (v, row) => <WeeklyScoreCell score={v} delta={row.productDelta} /> },
                { label: 'Objection', field: 'objection', align: 'center', render: (v, row) => <WeeklyScoreCell score={v} delta={row.objectionDelta} /> },
                { label: 'Closing', field: 'closing', align: 'center', render: (v, row) => <WeeklyScoreCell score={v} delta={row.closingDelta} /> },
                { label: 'Follow-up', field: 'followup', align: 'center', render: (v, row) => <WeeklyScoreCell score={v} delta={row.followupDelta} /> },
                { label: 'Professional', field: 'professionalism', align: 'center', render: (v, row) => <WeeklyScoreCell score={v} delta={row.professionalismDelta} /> },
                { label: 'Calls', field: 'callsAnalyzed', align: 'right' },
              ]}
              rows={scAgentProgress}
            />

            {scWeeklyTrend.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <ChartCard title="Weekly Score Trends" subtitle="Overall score progression — last 4 weeks" height={320}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={scWeeklyTrend} margin={{ top: 10, right: 20, left: -5, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={COLORS.slate[200]} />
                      <XAxis dataKey="week" tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                      <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: COLORS.slate[600] }} />
                      <RechartsTooltip contentStyle={{ borderRadius: 8, border: `1px solid ${COLORS.slate[200]}` }} formatter={(v) => Number(v).toFixed(1)} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      {scTrendAgents.map((agent, i) => (
                        <Line key={agent} type="monotone" dataKey={agent} stroke={CHART_COLORS[i % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3, strokeWidth: 2 }} activeDot={{ r: 5 }} />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </ChartCard>
              </Box>
            )}

            {scCoachingInsights.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <SectionHeader title="Coaching Insights" subtitle="AI-generated recommendations based on call pattern analysis" />
                <Box sx={{ mb: 2 }}>
                  <StyledTabs
                    value={coachingTabValue}
                    onChange={(_, v) => setCoachingTabValue(v)}
                    tabs={['All', ...scCoachingAgents]}
                  />
                </Box>
                {(
                  coachingTabValue === 0
                    ? scCoachingInsights
                    : scCoachingInsights.filter((insight) => insight.agent === scCoachingAgents[coachingTabValue - 1])
                ).map((insight, i) => (
                  <InsightCard key={i} insight={insight} />
                ))}
              </Box>
            )}
          </>
        )}
      </TabPanel>
    </PageContainer>
  );
}
